import { CloudUpload, Download, InsertDriveFile } from "@mui/icons-material";
import { Box, CircularProgress, LinearProgress, SvgIcon } from "@mui/joy";
import Button from "@mui/joy/Button";
// import Link from "next/link";
import { CssVarsProvider } from "@mui/joy/styles";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import CustomModal from "../../components/modals/Index";
import { getAuthData, uploadFile } from "../../features/auth/authSlice";
import Alert from "../../utils/alert";
import {
  camelCaseToNormalCase,
  getAmount,
  getFileSize,
} from "../../utils/helper";

const BulkUpload = ({ close, open }) => {
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [total, setTotal] = useState(1);
  const [showLoader, setShowLoader] = useState(false);
  const { loading } = useSelector(getAuthData);

  const dispatch = useDispatch();

  const onDrop = useCallback((acceptedFiles) => {
    setFile(acceptedFiles[0]);
    setShowLoader(false);
    setProgress(0);
    setTotal(1);
  }, []);

  const { getRootProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    onFileDialogOpen() {
      setShowLoader(false);
      setIsSuccess(false);
      setIsError(false);
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/csv": [".csv"],
      "application/vnd.ms-excel": [".xlsx", ".xls"],
      "application/x-csv": [".csv"],
      "text/x-csv": [".csv"],
    },
    maxFiles: 1,
    maxSize: 6000000,
    multiple: false,
  });

  function clearFile(event) {
    setFile(null);
    setProgress(0);
    setTotal(0);
    setShowLoader(false);
    setIsError(false);
    setIsSuccess(false);
    dispatch(close());
    return close;
  }
  async function handleUploadDevice(event) {
    event.preventDefault();
    try {
      setShowLoader(true);
      const config = {
        onUploadProgress: (e) => {
          const percentCompleted = Math.round((e.loaded / e.total) * 100);
          console.log(e.loaded, e.total, percentCompleted);
          setProgress(percentCompleted);
          setTotal(e.loaded);
        },
        headers: { "Content-Type": "multipart/form-data" },
      };
      if (file.size > 6000000) {
        Alert({
          type: "error",
          message: "File size cannot be more than 6MB",
        });
        return;
      }
      const formData = new FormData();
      formData.append("files[]", acceptedFiles[0]);
      // upload file here
      const res = await dispatch(
        uploadFile({ formData, config, query: "?action=reassign" })
      ).unwrap();
      if (res?.success) {
        clearFile(); // close upload modal
        setIsSuccess(true);
        setIsError(false);

        let html = document.createElement("div");
        html.className = "grid grid-col-2 items-center justify-center gap-2";
        for (let item of Object.keys?.(JSON.parse?.(res?.message))) {
          html.innerHTML += `<div class="flex gap-2 items-center">
            <span class="text-[15px] capitalize">${camelCaseToNormalCase(
              item
            )}:</span>
            <span class="text-[15px] font-semibold">${getAmount(
              JSON.parse(res?.message)[item]
            )}</span>
          </div>`;
        }
        Swal.fire({
          title: "File uploaded successfully",
          icon: "success",
          html,
        });
      } else {
        setIsError(true);
        setIsSuccess(false);
      }
    } catch (err) {
      console.log(err);
      setIsError(true);
      setShowLoader(false);
      Alert({
        type: "error",
        message: "File upload failed",
      });
    }
  }

  return (
    <CustomModal
      {...{
        title: "Bulk assignment",
        open,
        close: clearFile,
      }}
    >
      <div className="mb-5">
        <div className="px-10">
          <a
            href="/sample_assignment.xlsx"
            download
            className="border border-borderColor  flex items-center justify-center rounded-md px-2 py-1 mb-5"
          >
            <Download className="text-lg text-[#1A1919]" />
            <span className="ml-1 text-[#1A1919] text-sm">
              Download Jobs Template Here
            </span>
          </a>

          <div
            className={`border h-44 border-dashed transition-all flex justify-center ${
              isDragActive
                ? "!cursor-grabbing shadow border-black"
                : loading
                ? "!cursor-progress"
                : "cursor-pointer border-[#D1D1D1]"
            } p-5 rounded-md`}
            {...(!loading && getRootProps())}
          >
            {isDragActive ? (
              <h1>Drop file here</h1>
            ) : file ? (
              <div>
                <h2 className="text-center text-black mb-2 font-medium underline">
                  File Details
                </h2>
                <p className="text-[#5D5D5D] text-center">
                  {/* <AttachFile /> */}
                  {file.name}
                </p>
                <p className="text-[#5D5D5D] text-center">
                  {/* <Storage />  */}
                  {getFileSize({ size: file?.size || 1 })}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <CloudUpload className="text-[#5D5D5D]" />

                <div className="my-5">
                  <p className="text-[#1A1919] font-medium text-sm">
                    Choose a file or drag & drop it here.
                  </p>
                  <p className="text-center text-[#5D5D5D] text-xs">
                    CSV or XLS formats, up to 6 MB.
                  </p>
                </div>

                <CssVarsProvider>
                  <Button
                    fontSize="lg"
                    component="label"
                    role={undefined}
                    tabIndex={-1}
                    variant="outlined"
                    color="neutral"
                    startDecorator={
                      <SvgIcon>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z"
                          />
                        </svg>
                      </SvgIcon>
                    }
                  >
                    Browse File
                  </Button>
                </CssVarsProvider>
              </div>
            )}
          </div>

          {showLoader && (
            <div className="border rounded-lg mt-5 p-3">
              <div className="flex items-center mb-2">
                <InsertDriveFile className="text-[#5D5D5D] text-4xl" />
                <div className="ml-2 ">
                  <span className="text-sm text-[#1A1919]">{file?.name}</span>
                  <div className="flex items-start">
                    <span className="text-xs text-[#5D5D5D]">
                      {getFileSize({ size: total })} of{" "}
                      {getFileSize({ size: file?.size })}
                    </span>
                    {loading && (
                      <>
                        <span className="text-[#5D5D5D] text-xs ml-1.5">•</span>
                        <p className="text-xs flex items-center ml-2 text-[#1A1919]">
                          <CssVarsProvider>
                            <CircularProgress
                              sx={{
                                "--CircularProgress-size": "13px",
                                "--CircularProgress-trackThickness": "1px",
                                "--CircularProgress-progressThickness": "1px",
                                mr: 0.5,
                              }}
                            />
                          </CssVarsProvider>
                          <span>Uploading...</span>
                        </p>
                      </>
                    )}

                    {file && !loading && (isError || isSuccess) && (
                      <>
                        <span className="text-[#5D5D5D] text-xs ml-1.5">•</span>
                        <p
                          className={`text-xs ${
                            isError ? "text-red-600" : "text-green-600"
                          } font-semibold rounded-full flex items-center ml-2`}
                        >
                          {isError ? "Failed" : "Success"}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <CssVarsProvider>
                <LinearProgress
                  color={isError ? "danger" : isSuccess ? "success" : "neutral"}
                  determinate
                  value={progress}
                />
              </CssVarsProvider>
            </div>
          )}

          {/* Continue here */}

          {!isSuccess && (
            <Box className="flex justify-between space-x-5 mt-5">
              <CssVarsProvider>
                <Button
                  fontSize="lg"
                  variant="solid"
                  color="neutral"
                  className="!bg-black hover:bg-black !px-4 text-black hover:text-white block w-[150px]"
                  sx={{
                    border: "1px solid black",
                    padding: ".6em !important",
                  }}
                  onClick={clearFile}
                >
                  Cancel
                </Button>
              </CssVarsProvider>
              <CssVarsProvider>
                <Button
                  fontSize="lg"
                  onClick={handleUploadDevice}
                  variant="solid"
                  className="hover:bg-brand-700 bg-brand-600 text-white !px-4 w-[150px]"
                  sx={{
                    padding: ".6em !important",
                  }}
                  loading={loading}
                  color="danger"
                >
                  Upload
                </Button>
              </CssVarsProvider>
            </Box>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default BulkUpload;
